import '../tooltip/tooltip';
import '../popover/popover';

import { css, html, nothing, PropertyValueMap } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { customElement, property, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import debounce from 'lodash/debounce';
import { ariaLabelWithNewTab } from '../../common/utils/a11y-util';

import { ActionStatus } from '../../common/constants/action-status';
import { loading, PRICING_SUBJECT_INFO, smallSpinner, spinner } from '../../common/constants/api-constants';
import {
  getCart as shopGetCart,
  getCartContext,
  getCartError,
  getCartStatus,
} from './shop-cart/store/shop-cart.selectors';
import { BaseComponent } from '../base-component/base-component';
import store, { waitForCart } from '../../store/store';
import { booleanConverter } from '../../common/converters';
import { UserProfile } from '../user-profile/user-profile.interface';
import { StoreState } from '../../store/store.interface';
import { PLACEMENT_BOTTOM } from '../../common/constants/bs';
import { isProfessional, isSharpenPlus } from '../../common/utils/product-util';
import { isSuccessUser, selectUser } from '../user-profile/user-profile.selectors';
import { ROLES } from '../user-profile';
import {
  addToCartUrlContext,
  getCartPageUrl,
  isHEInstructor,
  removeFromOldCartCookie,
} from '../../common/utils/cart-util';
import emit from '../../common/events';
import { Modal } from '../modal/modal';
import { HE, isHeStorePurchaseFlow, isTunnelPurchaseFlow, PROFESSIONAL } from '../../common/utils/util';
import { getShopCart } from './shop-cart/thunks/get-shop-cart.thunk';
import { removeFromCart } from './shop-cart/thunks/remove-from-cart.thunk';
import { addToCart } from './shop-cart/thunks/add-to-cart.thunk';
import { addGuestInstructorSampleToCart } from './shop-cart/thunks/add-guest-instructor-sample-to-cart.thunk';
import environment from '../../common/environment';
import { ViewTypeLabel } from '../search/interfaces/search.interface';
import { WishList } from '../wish-list/interfaces/wish-list-items.interface';
import {
  captureAddToCartAnalytics,
  determineProgramSourceId,
  getOrderFlowAnalyticsData,
  isVirtualProduct,
  StudentInstructorToggleGroup,
} from '../../common/utils/analytics-util';
import { removeFromCartLocalStorageObject, saveCartLocalStorageObject } from '../search/utils/search.util';
import { selectSelectedCatalog } from '../search/store/search.selector';
import { ApiError } from '../../common/utils/api-utils';
import { getApiErrorCode } from '../../common/utils/error-code-utils';
import { OfferTypeChecker } from '../../common/utils/offer-type-util';
import { showConfirmInstructorModal } from '../pdp/utils';
import { MHP_EBOOK_INSTRUCTOR_SAMPLE_MARKETO_URL } from '../../common/constants/professional-redirects';
import { CoverImageAnalyticsConstants } from '../../common/constants/anayltics.constants';
import { shouldUseInstructorSampleCartFlow } from './utils/add-to-cart.util';
import { getApiErrorMessage } from '../../common/utils/error-message.util';

const CARD_DESCRIPTION_EBOOK_RENTAL = 'eBook Rental';
const CARD_DESCRIPTION_EBOOK_PURCHASE = "eBook Purchase";
const CARD_DESCRIPTION_TEXTBOOK_RENTAL = "Textbook Rental";
const CARD_DESCRIPTION_CONNECT_TEXTBOOK_RENTAL = "Connect + Textbook Rental";

export enum DesignType {
  DEFAULT = 'default',
  PRIMARY = 'primary'
}

/**
 * An element to add products to your cart.
 *
 * @csspart button - The button
 * 
 * @event click - A user has clicked the button.
 *        <br><br>&cross; dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: n/a
 * @event added-to-cart - A user has taken an action to add the ISBN to their cart.
 *        The ISBN and business unit of the product are in the event details.
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: { isbn, businessUnit, eventName }
 * @event removed-from-cart - A user has taken an action to remove the ISBN to their cart.
 *        The ISBN and business unit of the product are in the event details.
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: { isbn, businessUnit, eventName }
 */

@customElement('mhecomm-add-to-cart')
export class AddToCart extends connect(store)(BaseComponent) {
  static styles = [
    css`
      :host {
        display: block;
      }
      .cart-item {
        font-size: var(--theme-h5-font-size);
        display: flex;
        align-items: center;
      }
      .cart-quantity {
        width: 80px !important;
        float: left;
      }
      .cart-quantity-box {
        width: 60px !important;
      }
      .add-to-cart-btn {
        width: 140px !important;
      }
      [part='cart-value'] {
        margin-left: auto;
      }
      .total {
        border-top: 1px solid var(--theme-gray-200);
        padding-top: var(--theme-spacer);
      }
      [part='qty-box'] {
        padding: 10px;
        font-size: var(--theme-font-size-base);
        text-align: right;
        width: 5rem;
        border: 1px solid var(--theme-gray-200);
        display: block;
      }
      [part='qty-total'] {
        display: flex;
        flex-direction: column;
        position: relative;
      }
      [part='qty-total'] [part='qty-box'] {
        margin-left: auto;
      }
      [part='qty-total'] .cart-item {
        margin-bottom: var(--theme-spacer);
      }
      [part='qty-only'] {
        text-align: right;
        margin-bottom: var(--theme-spacer);
      }
      [part='qty-only'] [part='qty'] {
        margin-top: var(--theme-spacer);
      }
      [part='qty-only'] [part='qty-label'] {
        margin-left: auto;
      }
      [part='qty-only'] [part='qty-box'] {
        display: inline;
        margin-left: var(--theme-spacer);
      }
      [part='qty-only'] [part='price'] {
        display: block;
      }
      mhecomm-tooltip {
        text-align: left;
        margin-bottom: var(--theme-spacer);
        color: #006bd6;
      }
      mhecomm-tooltip div {
        text-align: left;
      }
      mhecomm-tooltip::part(button) {
        font-size: var(--theme-font-size-base);
      }
      .quantityContainer {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
      }
      .quantityInput {
        align-items: flex-end;
      }
      .remove-cart-button {
        font-size: 0.875rem !important;
      }
      .btn.btn-primary.no-underline-focus:focus {
        text-decoration: none;
      }
      @media (max-width: 720px) {
        [part='qty-only'] {
          display: flex;
          align-items: center;
          flex-direction: row-reverse;
          margin-top: var(--theme-spacer);
          margin-bottom: var(--theme-spacer);
        }
        [part='qty-only'] [part='qty'] {
          margin-right: auto;
          margin-top: 0;
        }
        .quantityContainer {
          display: flex;
          flex-direction: row;
          align-items: flex-end;
        }
        .quantityInput {
          align-items: flex-start;
        }
      }
      .qty-error {
        border: 1px solid var(--theme-primary);
      }

      input::-webkit-outer-spin-button,
      input::-webkit-inner-spin-button {
        margin-left: 5px;
        margin-right: -8px;
      }

      :host(.mh-btn-lg) [part='button'] {
        display: block;
        font-size: 1.2rem;
        padding: 1rem;
      }
      :host(.mh-btn-md) [part='button'] {
        display: block;
        font-size: 1rem;
        padding: 0.8rem;
      }
      :host(.mh-btn-sm) [part='button'] {
        display: block;
        font-size: 0.9rem;
        padding: 0.7rem;
      }
      :host(.mh-btn-xs) [part='button'] {
        display: block;
        font-size: 0.8rem;
        padding: 0.6rem;
      }
      :host(.mh-btn-block) [part='button'] {
        display: block;
        width: 100%;
        text-align: center;
      }
      :host(.mh-btn-right) [part='button'] {
        margin-left: auto;
        margin-right: 0;
      }
      :host(.mh-btn-center) [part='button'] {
        margin-left: auto;
        margin-right: auto;
      }
    `,
  ];

  /**
   * The text to display when this product is not yet in the cart.
   */
  @property()
  text = 'Add to cart';

  /**
   * The text to display when this product is in the cart.
   */
  @property()
  addedText = 'Remove from cart';

  @property()
  updateText = 'Update cart';

  /**
   * Once added, the product can be added to the cart again if this property is set to true. If both this and the removeable field are both set to true, the removeable field takes precedence.
   */
  @property({ converter: booleanConverter })
  allowMultiple = false;

  /**
   * Once added, the product can be removed from cart if this property is true.
   */
  @property({ converter: booleanConverter })
  removeable = true;

  /**
   * If true, the user will be taken directly to the checkout page after clicking the add to cart button.
   */
  @property({ converter: booleanConverter })
  instantCheckout = false;

  /**
   * The ISBN of the product that you want the user to be able to add to their cart. This is a required propery.
   */
  @property()
  isbn = '';

  @property()
  isbn13 = '';

  /**
   * The business unit of the provided ISBN. This is a required propery.
   */
  @property()
  businessUnit = '';

  @property()
  copyRightYear = '';

  @property()
  description = '';

  @property()
  gradeLevel: string | undefined = '';

  @property({ type: Number })
  edition: number | undefined;

  @property()
  editionOrRelease: string | undefined;

  @property()
  formattedEdition: string | undefined = '';

  @property()
  tabType?: string;

  @property()
  contributors: string | undefined;

  /**
   * Which store this product is for. This parameter is optional.
   */
  @property()
  store?: string;

  /**
   * The net price of the product. This parameter needs passed in if showQuanity is set.
   */
  @property({ type: Number })
  netPrice?: number;

  /**
   * If true, the quantity field will be shown.
   */
  @property({ converter: booleanConverter })
  showPrice = false;

  /**
   * Quantity value shall be captured using an input field.
   */
  @property({ type: Number })
  quantity = 1;

  /**
   * If true, the quantity field will be shown.
   */
  @property({ converter: booleanConverter })
  showQuantity = false;

  /**
   * If the quantity input is shown, it makes it disabled.
   */
  @property({ converter: booleanConverter })
  showQuantityInputAsDisabled = false;

  /**
   * If true, the quantity input will be hidden.
   */
  @property({ converter: booleanConverter })
  hideQuantityInput = false;

  /**
   * If true, the total price will be calculated. This requires showQuantity to be true.
   */
  @property({ converter: booleanConverter })
  showTotal = false;

  /**
   * If true, the product restrictions link will be shown.
   * TODO: Until we get further requirements for the restrictions, we are turning them off
   */
  //@property({ converter: booleanConverter })
  showRestrictions = false;

  /**
   * If true, the show School Pricing will be shown
   */
  @property({ converter: booleanConverter })
  showSchoolPricing = false;

  /**
   * If true, the product restrictions link will be shown.
   */
  @property({ converter: booleanConverter })
  showComingSoon = false;

  /**
   * If true, only remove link will be shown.
   */
  @property({ converter: booleanConverter })
  showRemoveLink = false;

  @property({ converter: booleanConverter })
  rowMode = false;

  @property({ converter: booleanConverter })
  fullWidth = false;

  /**
   * If true, the add to cart button is disabled.
   */
  @property({ converter: booleanConverter })
  disabled = false;

  /**
   * Is virtual only remove link
   */
  @property({ converter: booleanConverter })
  isVirtual = false;

  /**
   * Describes the ISBN purchase option's product type.
   */
  @property()
  poType?: string;

  /**
   * Instructor only attribute for user
   */
  @property({ converter: booleanConverter })
  instructorOnly = false;

  /**
   * External URL for redirecting user outside cart
   */
  @property()
  externalUrl?: string;

  /**
   * Instructor only attribute for user
   */
  @property({ converter: booleanConverter })
  externalRedirectEnabled = false;

  /**
   * The product title
   */
  @property()
  productTitle?: string;

  /**
   * The progarmId of the product
   */
  @property()
  programSourceId?: string;

  /**
   * If true, the product is added to wishlist.
   */
  @property({ type: Boolean })
  isWishListProduct?: boolean = false;

  /**
   * The wishlist id
   */
  @property({ type: Object })
  wishList?: WishList;

  /**
   * The track page, where add to card triggered
   */
  @property()
  addToCartPageType?: string;

  /**
   * Has google campaign ID
   */
  @property({ converter: booleanConverter })
  googleCampaignId = false;

  @property()
  view = '';

  /**
   * The product format for product type
   */
  @property()
  format?: string;

  @property({ type: String })
  designMode = DesignType.DEFAULT;

  /**
   * The PDP type
   */
  @property()
  pdpType?: string;

  @property({ type: Number })
  listPrice?: number;

  @property({ type: Boolean })
  isWishListItemCardView = false;

  @property()
  customStyles?: string;

  /**
   * Offer pricing identifier
   */
  @property({ type: String })
  offerPricingId = '';

  /**
   * Offer type identifier
   */
  @property({ type: String })
  offerType?;

  /**
   * Capture Event in local storage for analytics
   */
  @property({ converter: booleanConverter })
  capturePDPLocalStorage = false;

  /**
   * index of the product if called from product-card-display component else undefined
   */
  @property({ type: Number })
  productIndex?: number;

  /**
   * this prop needs be passed 'true' from AEM when the component is rendered
   * in the context of MHP PDP
   */
  @property({ type: String })
  isMhpProduct?: string;

  @property({ type: String })
  durationType?: string;

  @property({ converter: booleanConverter })
  isEvergreenProduct?: boolean;

  @property({ type: Number })
  duration?: number;

  @property({ type: String })
  durationPeriod?: string;

  @property({ type: Boolean })
  removeFromCartPreview = false;

  @property({ type: String })
  productType?: string;

  /**
   * use this value for the data-automation-id isbn instead of the generic isbn value.
   * If this is undefined, then isbn will be used
   */
  @property({ type: String })
  automationIdIsbn?: string;

  /**
   * If true, add to cart button will be enabled with spinner
   */
  @property({ converter: booleanConverter })
  showSpinner = false;

  @property({ converter: booleanConverter })
  isCreateProduct?: boolean;

  @property({ type: String })
  formatDesc?: string;

  @property({ type: String })
  formatTypeDesc?: string;

  /**
   * Calculates total price of the cart
   */
  @state()
  private total?: number;

  @state()
  private _addedToCart = false;

  @state()
  private buttonText = '';

  @state()
  private cartIsLoading = false;

  @state()
  private qtyError = false;

  @state()
  private productRestrictionText =
    'Some of our products have restrictions: Digital subscriptions are available for institutions & homeschoolers only. To purchase digital products, add it to your cart and sign in with an Educator or Homeschooler account. Some products, like answer keys, require a teacher certificate or proof of homeschooling for fulfillment. If we don’t have documentation on file, this may delay your order and we will email you.';

  @state()
  private productRestrictionLabel = 'Product Restrictions *';

  @state()
  private comingSoonText =
    'This product is not available yet, but you can preorder it. We will ship or provision it as soon as it is available.';

  @state()
  private showSchoolPricingText = 'Pricing for school accounts will display in the cart once you log in.';

  @state()
  private comingSoonLabel = 'Coming Soon';

  @state()
  private isInstructor = false;

  @state()
  private instructorLabel = 'Request Copy';

  @state()
  private stateIsDisabled = false;

  @state()
  private isHEInstructor = false;

  @state()
  private isRemoving = false;

  @state()
  private externalUrlModalText = 'You will be taken to our preferred partner eCampus.com to complete your transaction.';

  @state()
  private externalUrlDisabledModalText =
    'This rental is temporarily unavailable. Please view our other low-cost options.';

  @state()
  private addToCartFailed = false;

  @state()
  private isSpinnerLoading = false;

  private user?: UserProfile;
  private virtualProduct = false;
  private debouncedOnClick = debounce((e: Event) => this._onClick(e), 500);

  connectedCallback() {
    super.connectedCallback();
    this.calculateTotal();

    if (this.googleCampaignId) {
      this.buttonText = 'Buy Now';
    }
    this.virtualProduct = this.isVirtual ? true : isVirtualProduct(this.poType!);
  }

  protected async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    await store.dispatch(getShopCart({ store: this.store }));
    this.determineBtnText(store.getState() as StoreState);
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.updated(_changedProperties);
    this.removeDisabledAtr();
  }

  private isMhpPdp() {
    return this.isMhpProduct?.toLowerCase() === 'true';
  }

  renderComp() {
    return html` ${this.addToCartFailed ? this.renderErrorToast() : nothing} ${this.renderAddToCart()} `;
  }

  private renderErrorToast() {
    return html`
      <mhecomm-toast
        type="primary"
        header="Error Adding Item to Cart"
        .body=${this.getErrorMessage()}
        ?autoHide="${false}"
        @toast-close="${() => (this.addToCartFailed = false)}"
      ></mhecomm-toast>
    `;
  }

  renderMhpInstructorLink() {
    return html`<a
        href=${MHP_EBOOK_INSTRUCTOR_SAMPLE_MARKETO_URL}
        target="_blank"
        aria-label="${ariaLabelWithNewTab(this.instructorLabel)}"
        data-automation-id="${this.getDataAutomationId()}"
        class="btn btn-primary no-underline-focus w-100 w-md-auto"
        @click=${() => {
          this.emitHePdpAnalytics('mhecomm-request-mhp-ebook');
        }}
      >
        ${this.instructorLabel}
      </a>
      <div class="mt-2">
        Validated Instructors Only
      </div> `;
  }

  private renderAddToCart() {
    if (this.isMhpPdp()) {
      return this.renderMhpInstructorLink();
    } else if (this.showQuantity && this.showTotal && !this.showRemoveLink) {
      return this.renderQuantityAndTotal();
    } else if (this.showQuantity && !this.showRemoveLink) {
      return this.view === ViewTypeLabel.LIST ? this.renderQuantityOnlyListView() : this.renderQuantityOnly();
    } else if (this.showPrice) {
      return this.renderPriceOnly();
    } else if (this.showRemoveLink) {
      return this.renderRemoveLink();
    } else {
      return this.renderButtonOnly();
    }
  }

  private getErrorMessage() {
    const apiError = getCartError(store.getState() as StoreState) as ApiError;
    const errorMessageOptions = {
      bold: true,
      element: this,
      errorCode: getApiErrorCode(apiError),
    };

    return getApiErrorMessage(errorMessageOptions);
  }

  renderQuantityAndTotal() {
    return html`
      ${this.showComingSoon
        ? html` <mhecomm-tooltip label=${this.comingSoonLabel}><div>${this.comingSoonText}</div></mhecomm-tooltip> `
        : ''}
      ${this.showRestrictions
        ? html`
            <mhecomm-tooltip label=${this.productRestrictionLabel}
              ><div>${this.productRestrictionText}</div></mhecomm-tooltip
            >
          `
        : ''}
      <div class="row mb-3">
        <div class="col-5">Price:</div>
        <div class="col-7 text-end fw-semibold">$${this.netPrice?.toFixed(2)}</div>
      </div>
      <div class="row mb-3 align-items-center">
        <div class="col-5">Quantity:</div>
        <div class="d-flex col-7 justify-content-end">
          <input
            class=" cart-quantity form-control ${this.designMode === DesignType.PRIMARY ? '' : 'text-end'}"
            type="number"
            pattern="[0-9]"
            min="1"
            onkeydown="return event.keyCode !== 69"
            name="quantity"
            max="999"
            .value=${this.quantity.toString()}
            @change=${this.onChange}
            @input=${this.onChange}
            aria-label="Quantity"
            ?disabled="${this.showQuantityInputAsDisabled}"
          />
        </div>
      </div>
      <div class="row mb-3 border-1 border-top"></div>
      <div class="row mb-3 ">
        <div class="col-7 fw-semibold"><span>Estimated Total</span>:</div>
        <div class="d-flex col-5 justify-content-end fw-semibold">$${this.total?.toFixed(2)}</div>
      </div>
      ${this.showSchoolPricing
        ? html`
            <mhecomm-tooltip
              class="fs-small"
              icon="info-circle"
              label="School Pricing *"
              text=${this.showSchoolPricingText}
              placement=${PLACEMENT_BOTTOM}
            >
            </mhecomm-tooltip>
          `
        : ''}
      <button
        class="btn btn-primary w-100 mb-3"
        aria-label="${ifDefined(this.getAriaLabelForButton())}"
        @click=${this._onClick}
        part="button"
        data-automation-id="${this.getBaseDataAutomationId()}"
        ?disabled=${this.stateIsDisabled || this.disabled}
      >
        ${this.buttonText}
      </button>

      <p class="fw-semibold mb-0 fs-small">${PRICING_SUBJECT_INFO}</p>
    `;
  }

  renderQuantityOnly() {
    return html`
      ${this.showSchoolPricing
        ? html`
            <div class="d-flex justify-content-end">
              <mhecomm-popover
                icon="info-circle"
                label="School Pricing *"
                ariaLabel="More information about school pricing"
                text=${this.showSchoolPricingText}
                disableLinkUnderline=true
                placement=${PLACEMENT_BOTTOM}
              ></mhecomm-popover>
            </div>
          `
        : ''}
      ${this.showComingSoon
        ? html`
            <div class="d-flex justify-content-end">
              <mhecomm-tooltip label=${this.comingSoonLabel} text=${this.comingSoonText}></mhecomm-tooltip>
            </div>
          `
        : ''}
      ${this.showRestrictions
        ? html`
            <mhecomm-tooltip
              label=${this.productRestrictionLabel}
              text=${this.productRestrictionText}
            ></mhecomm-tooltip>
          `
        : ''}

      <hr
        class="hr-primary ${this.view === ViewTypeLabel.CARD ? '' : 'd-lg-none'} ${this.designMode ===
        DesignType.PRIMARY
        ? 'd-none'
        : ''}"
        aria-hidden="true"
      />

      <div class="row">
        <div
          class="d-flex pb-1 fs-large ${this.designMode === DesignType.PRIMARY
        ? 'fw-bold mb-lg-4'
        : 'fw-semibold'} col-md-12 justify-content-start justify-content-sm-start justify-content-md-start ${this
          .view === ViewTypeLabel.CARD
          ? ''
          : 'justify-content-lg-end'}"
        >
          <label aria-label="Item Price">
            $${this.renderListPrice()}
          </label>
        </div>

        <div class="col-3 col-sm-3 col-md-3 ${this.view === ViewTypeLabel.CARD ? '' : 'col-lg-12'}">
          <div class="row">
            <div
              class="d-flex justify-content-start justify-content-sm-start justify-content-md-start ${this.view ===
        ViewTypeLabel.CARD
        ? ''
        : 'justify-content-lg-end'}"
            >
              <label
                for="quantity-${this.isbn}"
                class="form-label ${this.designMode === DesignType.PRIMARY ? 'fw-semibold' : ''}"
                >Quantity</label
              >
            </div>
            <div
              class="d-flex  justify-content-start justify-content-sm-start justify-content-md-start ${this.view ===
        ViewTypeLabel.CARD
        ? ''
        : 'justify-content-lg-end'}"
            >
              <input
                class="${this.qtyError ? ' qty-error' : ''} ${this.designMode === DesignType.PRIMARY
        ? 'rounded-3 cart-quantity-box'
        : 'text-end'} form-control cart-quantity"
                id="quantity-${this.isbn}"
                type="number"
                onkeydown="return event.keyCode !== 69"
                pattern="[0-9]*"
                min="1"
                max="999"
                name="quantity"
                .value=${this.quantity.toString()}
                @change=${this.onChange}
                @input=${this.onChange}
                aria-label="Quantity"
                ?disabled="${this.showQuantityInputAsDisabled}"
              />
            </div>
          </div>
        </div>
        <div
          class="d-flex justify-content-end align-items-end pt-lg-3 col-9 col-sm-9 col-md-9 ${this.view ===
        ViewTypeLabel.CARD
        ? ''
        : 'col-lg-12'} "
        >
          ${this.buttonText
        ? html`
                <button
                  class="btn ${this.designMode === DesignType.PRIMARY ? 'btn-primary' : 'btn-outline-primary'}"
                  aria-label="${ifDefined(this.getAriaLabelForButton())}"
                  @click=${this._onClick}
                  data-automation-id="${this.getBaseDataAutomationId()}"
                  ?disabled=${this.stateIsDisabled || this.disabled || this.isSpinnerLoading}
                  style="width: ${this.isSpinnerLoading ? '7rem' : ''}"
                >
                  ${this.renderButtonLabelOrSpinner()}
                </button>
              `
        : loading({ columns: 1, m: 'mt-0', width: '75%' })}
        </div>
      </div>
    `;
  }

  renderQuantityOnlyListView() {
    return html`
      ${this.showComingSoon
        ? html` <mhecomm-tooltip label=${this.comingSoonLabel}><div>${this.comingSoonText}</div></mhecomm-tooltip> `
        : ''}
      ${this.showRestrictions
        ? html`
            <mhecomm-tooltip label=${this.productRestrictionLabel}
              ><div>${this.productRestrictionText}</div></mhecomm-tooltip
            >
          `
        : ''}
      ${this.designMode === DesignType.PRIMARY
        ? html` <div class="d-none d-lg-flex gap-3 align-items-center justify-content-end">
            <label class="fw-bold" aria-label="Item Price">
              $${this.renderListPrice()}
            </label>

            <input
              class="rounded-3 cart-quantity-box form-control ${this.qtyError ? 'qty-error' : ''}"
              id="quantity-${this.isbn}"
              type="number"
              onkeydown="return event.keyCode !== 69"
              pattern="[0-9]*"
              min="1"
              max="999"
              name="quantity"
              .value=${this.quantity.toString()}
              @change=${this.onChange}
              @input=${this.onChange}
              aria-label="Quantity"
              ?disabled="${this.showQuantityInputAsDisabled}"
            />
            ${this.buttonText
            ? html` <button
                  class="btn btn-primary add-to-cart-btn"
                  aria-label="${ifDefined(this.getAriaLabelForButton())}"
                  @click=${this._onClick}
                  data-automation-id="${this.getBaseDataAutomationId()}"
                  ?disabled=${this.stateIsDisabled || this.disabled}
                >
                  ${this.buttonText}
                </button>`
            : loading({ columns: 1, m: 'mt-0', width: '140px', height: '40px' })}
          </div>`
        : nothing}

      <div class="row ${this.designMode === DesignType.PRIMARY ? 'd-lg-none align-items-lg-center' : ''}">
        <div class="col-12 col-md-3">
          <div class="row">
            <div class="col-4 d-md-none ${this.designMode === DesignType.PRIMARY ? 'd-none' : ''}">
              <div class="fw-semibold">Price:</div>
            </div>
            <div class="col-8 col-md-12 ${this.designMode === DesignType.PRIMARY ? 'text-lg-end' : ''}">
              <label
                class="${this.designMode === DesignType.PRIMARY ? 'fw-bold' : ''}"
                aria-live="polite"
                aria-atomic="true"
                aria-label="Item Price"
              >
                $${this.renderListPrice()}
              </label>
            </div>
          </div>
        </div>
        <div class="col-12 ${this.designMode === DesignType.PRIMARY ? 'col-lg-9 mt-lg-0' : 'col-md-9 mt-md-0'} mt-2 ">
          <div class="row">
            <div class="col-4 d-md-none ${this.designMode === DesignType.PRIMARY ? 'd-none' : ''}">
              <div class="fw-semibold">
                <label
                  for="quantity-${this.isbn}"
                  class="form-label ${this.designMode === DesignType.PRIMARY ? 'fw-semibold' : ''}"
                  >Quantity:</label
                >
              </div>
            </div>
            <div class="col-${this.designMode === DesignType.PRIMARY ? 12 : 8} col-md-12">
              <div class="row">
                <div class="${this.designMode === DesignType.PRIMARY ? 'd-lg-none' : 'd-none'}">
                  <label for="quantity-${this.isbn}" class="form-label mb-1 fw-semibold">Quantity</label>
                </div>
                <div class="col-${this.designMode === DesignType.PRIMARY ? 3 : 4}">
                  <input
                    class="${this.qtyError ? ' qty-error' : ''} ${this.designMode === DesignType.PRIMARY
        ? 'rounded-3 cart-quantity-box'
        : 'text-end'} form-control"
                    id="quantity-${this.isbn}"
                    type="number"
                    onkeydown="return event.keyCode !== 69"
                    pattern="[0-9]*"
                    min="1"
                    max="999"
                    name="quantity"
                    .value=${this.quantity.toString()}
                    @change=${this.onChange}
                    @input=${this.onChange}
                    aria-label="Quantity"
                    ?disabled="${this.showQuantityInputAsDisabled}"
                  />
                </div>
                <div
                  class="col-${this.designMode === DesignType.PRIMARY ? 9 : 8} text-end ${this.designMode ===
        DesignType.PRIMARY
        ? 'd-lg-grid'
        : 'd-grid'}"
                >
                  ${this.buttonText
        ? html` <button
                        class="btn ${this.designMode === DesignType.PRIMARY ? 'btn-primary' : 'btn-outline-primary'}"
                        @click=${this._onClick}
                        data-automation-id="${this.getBaseDataAutomationId()}"
                        ?disabled=${this.stateIsDisabled || this.disabled}
                      >
                        ${this.buttonText}
                      </button>`
        : loading({ columns: 1, m: 'mt-0', width: '75%' })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderPriceOnly() {
    return html`
      ${this.showComingSoon
        ? html` <mhecomm-tooltip label=${this.comingSoonLabel}><div>${this.comingSoonText}</div></mhecomm-tooltip> `
        : ''}
      ${this.showRestrictions
        ? html`
            <mhecomm-tooltip label=${this.productRestrictionLabel}
              ><div>${this.productRestrictionText}</div></mhecomm-tooltip
            >
          `
        : ''}
      <div class="${this.rowMode ? 'd-flex flex-row' : ''}">
        <div class="${this.rowMode ? 'me-3' : 'mb-1'}">
          <div class="d-flex ${this.fullWidth ? 'justify-content-center' : 'justify-content-end'} fw-bold fs-3">
            $${this.netPrice?.toFixed(2)}
          </div>
        </div>
        ${this.buttonText
        ? html`
              <button
                class="btn btn-primary btn-lg text-nowrap ${this.fullWidth ? 'w-100' : ''}"
                aria-label="${ifDefined(this.getAriaLabelForButton())}"
                @click=${this._onClick}
                part="button"
                data-automation-id="${this.getBaseDataAutomationId()}"
                ?disabled=${this.stateIsDisabled || this.disabled}
              >
                ${this.buttonText}
              </button>
            `
        : loading({ columns: 1, m: 'mt-1', width: '75%' })}
      </div>
    `;
  }

  renderButtonOnly() {
    return this.buttonText
      ? html`
          <button
            class="btn btn-primary btn-lg ${this.customStyles} ${this.isDisabled() || this.isRemoving || this.disabled
              ? 'disabled'
              : ''}"
            aria-label="${ifDefined(this.getAriaLabelForButton())}"
            aria-disabled="${this.isDisabled() || this.isRemoving || this.disabled}"
            @click=${this._onClick}
            part="button"
            aria-haspopup="${ifDefined(this.isInstructor && !this.user ? 'dialog' : undefined)}"
            data-automation-id="${this.getDataAutomationId()}"
            ?disabled=${this.isDisabled() || this.isRemoving || this.disabled || this.isSpinnerLoading}
            style="width: ${this.isSpinnerLoading ? '15.5rem' : ''}"
          >
            ${this.renderButtonLabelOrSpinner()}
          </button>
        `
      : loading({ columns: 1, m: 'mt-1', width: '75%' });
  }

  isMhpButNotOtherProfessionalProduct() {
    return (
      !isProfessional(this.businessUnit) ||
      OfferTypeChecker.isMhpEbook(this.offerType) ||
      OfferTypeChecker.isMhpPrint(this.offerType)
    );
  }

  renderRemoveLink() {
    return html`
      <div>
        ${!this.hideQuantityInput && (this.isVirtual && this.businessUnit !== 'SEG' && this.isMhpButNotOtherProfessionalProduct())
        ? html`<div class="d-flex justify-content-end">
              <label class="form-label" for="quantity-${this.isbn}">
                Quantity: ${this.quantity}
              </label>
            </div> `
        : nothing}
        <div class="d-flex justify-content-end">
          <div class="col-5 col-sm-2 col-md-3 col-lg-6">
            ${this.hideQuantityInput || (this.isVirtual && this.businessUnit !== 'SEG')
        ? nothing
        : html`
                  <input
                    type="number"
                    class="form-control text-end"
                    id="quantity-${this.isbn}"
                    onkeydown="return event.keyCode !== 69"
                    pattern="[0-9]"
                    min="1"
                    max="999"
                    name="quantity"
                    maxlength="3"
                    .value=${this.quantity.toString()}
                    @input=${this.calculateQuantity.bind(this)}
                    aria-label="Quantity"
                    ?disabled="${!this.showQuantity}"
                    style="cursor: ${this.showQuantity ? 'auto' : 'not-allowed'}"
                  />
                `}
          </div>
        </div>
        
        ${isTunnelPurchaseFlow() && !isSharpenPlus(this.programSourceId) 
          ? html`${nothing}`
          : html`
            <div class="d-flex ${this.hideQuantityInput ? 'justify-content-start' : 'justify-content-end'}">
              <button
                id="remove-from-cart"
                class="btn btn-link p-0 ${this.hideQuantityInput ? 'remove-cart-button border-0 mt-0' : 'mt-2'}"
                aria-label="Remove ${this.productTitle || this.isbn}"
                @click=${(e: Event) => { e.stopPropagation(); this._onClickRemove(); }}
                ?disabled=${this.isDisabled() || this.isRemoving}
              >
                Remove ${this.isRemoving ? smallSpinner() : ''}
              </button>
            </div>
          `
        }
      </div>
    `;
  }

  private renderButtonLabelOrSpinner() {
    return html`
      ${this.showSpinner 
        ?
        this.isSpinnerLoading ? spinner({ size: 'sm' }) : this.buttonText
        :
        this.buttonText
      }`;
  }

  stateChanged(state: StoreState) {
    this.user = selectUser(state);
    this.isInstructor =
      this.dataset.toggleView === StudentInstructorToggleGroup.INSTRUCTOR ? true : this.user?.userType == ROLES.INS;
    this.isHEInstructor = isHEInstructor(this.user);
    if (!this.user && this.isInstructor) {
      if (this.isPoTypeEbook()) {
        this.instructorLabel = 'Request Access';
      } else if (this.isMhpPdp()) {
        this.instructorLabel = this.getMhpInstructorLabel();
      } else {
        this.instructorLabel = 'Request Copy';
      }
    } else if (this.isHEInstructor) {
      if (this.poType == CARD_DESCRIPTION_EBOOK_RENTAL || this.poType == CARD_DESCRIPTION_EBOOK_PURCHASE) {
        this.instructorLabel = 'Access eBook';
      } else if (isHeStorePurchaseFlow()) {
        this.instructorLabel = 'Add To Cart';
      } else {
        this.instructorLabel = this.isMhpPdp() ? this.getMhpInstructorLabel() : 'Request Copy';
      }
    } else {
      this.instructorLabel = this.isRental() ? 'Rent Now' : 'Buy Now';
    }
    this.determineBtnText(state);
    this.calculateTotal();
    this.stateIsDisabled =
      !this.externalUrl &&
      (this.cartIsLoading ||
        (this._addedToCart && !this.allowMultiple && !this.removeable) ||
        !this.quantity ||
        (isSuccessUser(state) && this.instructorOnly && !this.isInstructor));
  }

  isPoTypeEbook() {
    return this.poType == CARD_DESCRIPTION_EBOOK_RENTAL || this.poType == CARD_DESCRIPTION_EBOOK_PURCHASE;
  }

  getMhpInstructorLabel() {
    return `Request eBook Sample`;
  }

  /**
   * Whether or not the add to cart button is currently in a disabled state.
   */
  isDisabled() {
    return this.cartIsLoading || (this._addedToCart && !this.allowMultiple && !this.removeable) || !this.quantity || (isHeStorePurchaseFlow() && this.isHEInstructor);
  }

  /**
   * The text of the button that is currently being displayed.
   */
  getButtonText() {
    if (this.externalUrl) {
      return this.isRental() && !this.googleCampaignId ? 'Rent Now' : this.text;
    } else if (this._addedToCart && this.showQuantity) {
      return this.updateText;
    } else if (!this._addedToCart && this.showQuantity) {
      return this.text;
    } else if (this._addedToCart) {
      return this.addedText;
    } else if (this.isInstructor) {
      return this.instructorLabel;
    } else {
      return this.isRental() && !this.googleCampaignId ? 'Rent Now' : this.text;
    }
  }

  async determineBtnText(state: StoreState) {
    await waitForCart();

    if (getCartStatus(state) === ActionStatus.succeeded) {
      const cartProductItem = this.getMatchingCartProduct(state);
      this._addedToCart = false;
      if (cartProductItem && !this.isRental()) {
        this._addedToCart = true;
      }
      if (!this.showQuantity) {
        this.stateIsDisabled = this._addedToCart;
      }
      this.quantity = cartProductItem !== undefined ? cartProductItem.quantity : this.quantity;
      this.buttonText = this.getButtonText();
    }
  }

  /**
   * Forces the component to update the cart. This is useful if the cart was updated outside of ecomm-ui and then client code needs to tell ecomm-ui cart to update itself.
   */
  updateCart() {
    store.dispatch(getShopCart({ store: this.store }));
  }

  private getMatchingCartProduct(state: StoreState) {
    return shopGetCart(state)?.products.find((product) => {
      if (product.productIdentifier.offerPricingId) {
        return product.productIdentifier.offerPricingId === this.offerPricingId;
      }
      return product.productIdentifier.isbn10 === this.isbn || product.productIdentifier.isbn13 === this.isbn;
    });
  }

  private onChange(e: any) {
    if (e.target.value?.length > 3) {
      e.target.value = e.target.value.slice(0, 3);
    }
    if (e.target.value >= 0 && (this.netPrice !== undefined || this.showRemoveLink)) {
      this.qtyError = false;
      this.quantity = parseInt(e.target.value);
      this.calculateTotal();
      this.buttonText = this.getButtonText();
    } else if (e.target.value == 0 && !this._addedToCart) {
      this.qtyError = true;
    }
  }

  /**
   * To remove disabled attribute, when disabled value is false
   */
  private removeDisabledAtr() {
    if (this.getAttribute('disabled') === 'false') {
      this.removeAttribute('disabled');
      this.disabled = false;
    }
  }

  private calculateTotal() {
    if (this.netPrice !== undefined) {
      this.total = (this.quantity || 1) * this.netPrice;
    }
  }

  private calculateQuantity(e: any) {
    const value = parseInt(e.target.value);
    
    // Validate range
    if (!e.target.value || isNaN(value) || value < 1 || value > 999) {
      return;
    }

    this.onChange(e);

    this.debouncedOnClick(e);
  }

  private _onClickRemove() {
    this.isRemoving = true;
    if (this.showRemoveLink) {
      removeFromCartLocalStorageObject(
        determineProgramSourceId(
          this.isbn,
          this.isbn13,
          this.businessUnit,
          this.programSourceId!,
        ),
        this.isbn,
        this.isbn13
      );
    }
    this.emitRemovedFromCart();
    store
      .dispatch(
        removeFromCart(
          addToCartUrlContext(getCartContext(store.getState() as StoreState, this.store), {
            businessUnit: this.businessUnit,
            isbn: this.isbn,
            quantity: 0,
            isbn13: this.isbn13,
            offerPricingId: this.offerPricingId,
          })
        )
      )
      .finally(() => {
        this.isRemoving = false;
        // I can't see this cookie being set anywhere, so I'm not sure what it does
        // I did check in production and it was not created anytime during the checkout flow
        removeFromOldCartCookie(this.isbn);
      });
  }

  getRentNowModalTemplate() {
    return html`<p>${this.externalRedirectEnabled ? this.externalUrlModalText : this.externalUrlDisabledModalText}</p>`;
  }

  emitRemovedFromCart() {
    emit({
      element: this,
      name: 'removed-from-cart',
      detail: this.dataLayerDetail(),
    });
  }

  emitAddToCart() {
    emit({
        element: this,
        name: 'added-to-cart',
        detail: this.dataLayerDetail(),
        dataLayer: true,
      });
  }

  emitHePdpAnalytics(eventName: string) {
    emit({
      element: this,
      name: eventName,
      detail: this.dataLayerDetail(),
      dataLayer: true,
    });
  }

  private async _onClick(e: Event) {
    this.setBtnSpinner(true);
    if (!this.showRemoveLink) {
      saveCartLocalStorageObject({
        programSourceId: this.programSourceId!,
        capturePDPLocalStorage: this.capturePDPLocalStorage,
        isbn10: this.isbn,
        isbn13: this.isbn13,
        index: this.productIndex || 1,
      });
    }
    if (this.stateIsDisabled || this.disabled) {
      return;
    }
    if (this._addedToCart && this.removeable && (!this.showQuantity || (this.showQuantity && this.quantity === 0))) {
      removeFromCartLocalStorageObject(
        determineProgramSourceId(
          this.isbn,
          this.isbn13,
          this.businessUnit,
          this.programSourceId!,
        ),
        this.isbn,
        this.isbn13
      );
      store.dispatch(
        removeFromCart(
          addToCartUrlContext(getCartContext(store.getState() as StoreState, this.store), this.getContextParams())
        )
      );
      this.emitRemovedFromCart();
      this.quantityError();
      this.qtyError = false;
    } else if (this.externalUrl) {
      const buttons = this.externalRedirectEnabled
        ? {
          primaryButton: { label: 'OK' },
          secondaryButton: { label: 'NO THANKS' },
        }
        : {
          secondaryButton: { label: 'OK' },
        };

      /*
        for rent now we open modal and when modal is closed we want to emit all the info
        other than externalUrl.
      */
      const dataLayerDetails = this.dataLayerDetail();
      delete dataLayerDetails['externalUrl'];

      const modal: Modal = new Modal({
        header: 'Rent Now',
        body: { template: this.getRentNowModalTemplate() },
        ...buttons,
        modalContext: { componentRef: 'add-to-cart', componentRefData: dataLayerDetails },
      });

      emit({
        element: this,
        name: 'add-to-cart-modal',
        detail: dataLayerDetails,
        dataLayer: true,
      });

      modal.addEventListener('mhecomm-modal-confirmed', () => {
        if (this.externalUrl) {
          emit({
            element: this,
            name: 'added-to-cart-external',
            detail: this.dataLayerDetail(),
            dataLayer: true,
          });
          window.open(this.externalUrl, '_blank');
          modal.close(e, false);
          this.focus();
        }
      });

      modal.open(e);
      this.setBtnSpinner(false);
    } else if (this.isInstructor && !this.user) {
      // this unconventional event name for backward compatibility
      this.emitHePdpAnalytics(`HE Instr ${this.isPoTypeEbook() ? `eBook` : `Print`} Sample Pop Up Request Button Clicks BODY`);

      // for he evergreen pdp guest view when user toggle to instructor view
      if (
        !(await showConfirmInstructorModal(e))
      ) {
        return;
      }

      this.emitHePdpAnalytics(`mhecomm-request-copy-${this.isPoTypeEbook() ? `ebook` : `print`}`);

      try {
        const guestCartId = await store.dispatch(
          addGuestInstructorSampleToCart(
            addToCartUrlContext(getCartContext(store.getState() as StoreState, this.store), {
              businessUnit: this.businessUnit,
              isbn: this.isbn,
              quantity: 1,
              offerPricingId: this.offerPricingId,
            })
          )
        ).unwrap();

        const link = new URL(environment().baseLoginUrl);
        link.searchParams.set('cart', 'true');
        link.searchParams.set('flow', 'sampling');
        link.searchParams.set('isbn', this.isbn);
        link.searchParams.set('magentoCart', environment().checkoutFlow);
        link.searchParams.set('guestcartid', guestCartId);
        window.location.href = link.toString();
      } catch (error) {
        console.error('Failed to add guest instructor sample to Magento cart', error);
      }
    } else if (this.isLoggedInInstructorSample()) {
      await this.handleLoggedInInstructorSample();
    } else if (this.isHEInstructor && this.isMhpProduct) {
      this.emitHePdpAnalytics('mhecomm-add-to-cart-heinstructor');

      store
        .dispatch(
          addToCart(
            addToCartUrlContext(
              getCartContext(store.getState() as StoreState, this.store),
              this.getHEInstructorContextParams()
            )
          )
        )
        .then(this.handleResponse.bind(this));
    } else {
      if (this.quantity > 0) {
        this.qtyError = false;
        store
          .dispatch(
            addToCart(
              addToCartUrlContext(getCartContext(store.getState() as StoreState, this.store), this.getContextParams())
            )
          )
          .then(this.handleResponse.bind(this));
        this.buttonText = this.getButtonText();
      } else {
        this.qtyError = true;
      }
    }
  }

  private getContextParams() {
    return {
      businessUnit: this.businessUnit,
      isbn: this.isbn,
      quantity: this.quantity,
      isbn13: this.isbn13,
      offerPricingId: this.offerPricingId,
    };
  }

  private getHEInstructorContextParams() {
    return { businessUnit: this.businessUnit, isbn: this.isbn, quantity: 1 };
  }

  private isLoggedInInstructorSample() {
    return shouldUseInstructorSampleCartFlow({
      isHEInstructor: this.isHEInstructor,
      isMhpPdp: this.isMhpPdp(),
      isHeStorePurchaseFlow: isHeStorePurchaseFlow(),
    });
  }

  private async handleLoggedInInstructorSample() {
    await store
      .dispatch(
        addToCart(
          addToCartUrlContext(getCartContext(store.getState() as StoreState, this.store), {
            businessUnit: this.businessUnit,
            isbn: this.isbn,
            isbn13: this.isbn13,
            quantity: 1,
            offerPricingId: this.offerPricingId,
            instructorSampling: true,
          })
        )
      )
      .then(this.handleResponse.bind(this));
  }

  private dataLayerDetail() {
    const dataLayerProduct = window.adobeDataLayer?.getState?.()?.product;
    const dataLayerPurchaseOption =
      dataLayerProduct?.purchaseOptions?.find((option: any) =>
        (this.isEdoms() && this.offerPricingId === option.offerPricingId) ||
        (!this.isEdoms() && this.isbn === option.isbn || this.isbn === option.isbn13)) || {};
    const state = store.getState() as StoreState;
    const catalog = selectSelectedCatalog(state);
    const businessUnit = this.isMhpPdp() ? PROFESSIONAL : this.businessUnit;
    const cartProduct = this.getMatchingCartProduct(state);
    const product = {
      ...dataLayerProduct,
      ...(dataLayerProduct?.titleType === undefined &&
        cartProduct?.productMetadata.titleType !== undefined && {
          titleType: cartProduct.productMetadata.titleType,
        }),
      ...(dataLayerProduct?.division === undefined &&
        cartProduct?.productMetadata.division !== undefined && {
          division: cartProduct.productMetadata.division,
        }),
    };
    return captureAddToCartAnalytics({
      product,
      purchaseOption: dataLayerPurchaseOption,
      state,
      isbn: this.isbn,
      productTitle: this.productTitle,
      businessUnit,
      copyrightYear: this.copyRightYear,
      instructorOnly: this.showRestrictions,
      isbn13: this.isbn13,
      netPrice: this.netPrice,
      description: this.description,
      gradeLevel: this.gradeLevel,
      quantity: this.quantity,
      store: this.store,
      contributors: this.contributors,
      edition: this.edition,
      editionOrRelease: this.editionOrRelease,
      pdpType: this.pdpType,
      dataset: this.dataset,
      isWishListProduct: this.isWishListProduct,
      wishList: this.wishList,
      addToCartPageType: this.addToCartPageType,
      showQuantity: this.showQuantity,
      showTotal: this.showTotal,
      showPrice: this.showPrice,
      externalUrl: this.externalUrl,
      instantCheckout: this.instantCheckout,
      showRemoveLink: this.showRemoveLink,
      format: this.format,
      programSourceId: this.programSourceId,
      hePDPViewType: this.dataset.toggleView as StudentInstructorToggleGroup,
      offerType: this.offerType,
      offerPricingId: this.offerPricingId,
      listPrice: this.listPrice,
      isEdoms: this.isEdoms(),
      catalog: catalog,
      isVirtual: this.virtualProduct,
      poType: this.poType,
      productType: this.getProductTypeForAnalytics(),
      ...(this.durationType && { durationType: this.durationType }),
      ...(this.isEvergreenProduct && { isEvergreenProduct: this.isEvergreenProduct }),
      ...(this.duration && { duration: this.duration }),
      ...(this.durationPeriod && { durationPeriod: this.durationPeriod }),
      removeFromCartPreview: this.removeFromCartPreview,
      ...((this.offerType || this.tabType) && { coverImage: this.getCoverImage() }),
      ...getOrderFlowAnalyticsData({
        programSourceId: this.programSourceId,
        isTunnelPurchaseFlow: isTunnelPurchaseFlow(),
      }),
      ...(this.isCreateProduct && { isCreateProduct: this.isCreateProduct }),
      ...(this.formatDesc && { formatDesc: this.formatDesc }),
      ...(this.formatTypeDesc && { formatTypeDesc: this.formatTypeDesc }),
    });
  }

  private getProductTypeForAnalytics(): string | undefined {
    if (!this.businessUnit && !this.offerType && !this.format) {
      return this.tabType?.toUpperCase?.();
    }
    if (this.businessUnit === PROFESSIONAL) {
      if (OfferTypeChecker.isFirstAidForward(this.offerType)) {
        return 'FAF';
      }
      if (OfferTypeChecker.isClinicalReasoning(this.offerType)) {
        return 'CLINICAL-REASONING';
      }
      return this.offerType;
    }
    return this.format ?? this.tabType?.toUpperCase?.();
  }

  private getCoverImage(): string {
    return (this.businessUnit == HE && OfferTypeChecker.isHeEbook(this.offerType ?? this.tabType)) ? CoverImageAnalyticsConstants.NOT_SHOWN : CoverImageAnalyticsConstants.SHOWN;
  }

  private isEdoms(): boolean {
    return !!this.offerPricingId;
  }

  private quantityError() {
    if (this.quantity == 0 || this.quantity == undefined) {
      this.qtyError = true;
    }
  }

  private isRental() {
    return this.poType == CARD_DESCRIPTION_TEXTBOOK_RENTAL || this.poType == CARD_DESCRIPTION_CONNECT_TEXTBOOK_RENTAL;
  }

  private async handleResponse() {
    await waitForCart();

    this.setBtnSpinner(false);

    const state = store.getState() as StoreState;
    if (getCartStatus(state) === ActionStatus.failed) {
      this.addToCartFailed = true;
    } else {
      const redirectUrl: string = getCartPageUrl(this.user, this.store);
      this.emitAddToCart();
      this.doRedirect(redirectUrl);
    }
  }

  public doRedirect(redirectUrl: string) {
    if (this.instantCheckout) {
      window.location.href = redirectUrl;
    }
  }

  private renderListPrice() {
    if (this.isWishListItemCardView && this.listPrice) {
      return this.listPrice;
    }
    return this.netPrice?.toFixed(2);
  }

  private getBaseDataAutomationId() {
    return `add-to-cart-${this.automationIdIsbn ? this.automationIdIsbn : this.isbn}`;
  }

  private getDataAutomationId() {
    let automationId = this.getBaseDataAutomationId();
    if (this.dataset?.purchaseOptionDuration && this.dataset?.purchaseOptionDuration?.trim()) {
      automationId += `-${this.dataset?.purchaseOptionDuration.toLowerCase?.().replaceAll(' ', '-')}`;
    }
    if (this.dataset?.toggleView){
      automationId += `-${this.dataset.toggleView}`;
    }
    return automationId;
  }

  private getAriaLabelForButton(): string | undefined {
    if (!this.productTitle) return undefined;

    const ariaLabelParts = [this.buttonText, this.productTitle];

    if (this.formattedEdition && parseInt(this.formattedEdition, 10) > 0) {
      ariaLabelParts.push(`${this.formattedEdition} Edition`);
    }

    if (this.formattedEdition && this.tabType) {
      ariaLabelParts[ariaLabelParts.length - 1] += ` ${this.tabType}`;
    }

    return ariaLabelParts.join(', ');
  }

  private setBtnSpinner(value: boolean) {
    this.isSpinnerLoading = value;
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-add-to-cart': AddToCart;
  }
}
